import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, SwitchVertical01 } from "@untitled-ui/icons-react";
import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

interface PaginationMeta {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

interface PaginatedDataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	meta: PaginationMeta;
	onPageChange: (page: number) => void;
	onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
	onLimitChange?: (limit: number) => void;
	noResultsText: string;
	icon?: React.ElementType;
	variant?: "default" | "gray-no-pagination" | "default-with-sort" | "static-clean";
	isLoading?: boolean;
	onRowClick?: (rowData: TData) => void;
	enableSorting?: boolean;
	fixedLayout?: boolean;
}

export const PaginatedDataTable = <TData, TValue>({
	columns,
	data,
	meta,
	onPageChange,
	onSortChange,
	onLimitChange,
	noResultsText,
	icon: Icon,
	variant = "default",
	isLoading = false,
	onRowClick,
	enableSorting = false,
	fixedLayout = false,
}: PaginatedDataTableProps<TData, TValue>) => {
	const [sorting, setSorting] = useState<SortingState>([]);

	const handleSortingChange = (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
		const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
		setSorting(newSorting);
		if (onSortChange && newSorting.length > 0) {
			const sort = newSorting[0];
			if (sort) {
				onSortChange(sort.id, sort.desc ? 'desc' : 'asc');
			}
		}
	};

	const limitOptions = [
		{ value: "5", label: "5 par page" },
		{ value: "10", label: "10 par page" },
		{ value: "20", label: "20 par page" },
		{ value: "50", label: "50 par page" },
	];

	const handleLimitChange = (value: string) => {
		if (onLimitChange) {
			onLimitChange(Number(value));
		}
	};

	const table = useReactTable({
		data,
		columns,
		manualPagination: true,
		manualSorting: enableSorting,
		state: {
			sorting,
		},
		onSortingChange: handleSortingChange,
		getSortedRowModel: getSortedRowModel(),
		getCoreRowModel: getCoreRowModel(),
		enableSorting,
		pageCount: meta.totalPages,
	});

	const isGrayVariant = variant === "gray-no-pagination";
	const isStaticCleanVariant = variant === "static-clean";

	return (
		<div className="w-full min-w-0">
			<div
				className={`rounded-md border poppins font-medium overflow-hidden ${
					isGrayVariant ? "bg-gray-50" : "bg-white"
				}`}
			>
				<div className="overflow-x-auto">
					<Table className={cn(fixedLayout && "table-fixed")}>
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										const isSortable = header.column.getCanSort();
										const sortDirection = header.column.getIsSorted();
										return (
											<TableHead
												key={header.id}
												onClick={
													enableSorting && isSortable
														? header.column.getToggleSortingHandler()
														: undefined
												}
												className={` ${
													isGrayVariant ? "px-3 py-4" : "p-4"
												} cursor-${isSortable && enableSorting ? "pointer" : "default"} select-none`}
											>
												<div className="flex items-center gap-1">
													{flexRender(header.column.columnDef.header, header.getContext())}
													{enableSorting && isSortable && (
														<span className="text-xs">
															{sortDirection === "asc" ? (
																<ArrowUp className="w-4" />
															) : sortDirection === "desc" ? (
																<ArrowDown className="w-4" />
															) : (
																<SwitchVertical01 className="w-4" />
															)}
														</span>
													)}
												</div>
											</TableHead>
										);
									})}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell
										colSpan={columns.length}
										className="h-24 text-center bg-white"
									>
										<div className="flex flex-col items-center justify-center gap-2 text-sm text-gray-500">
											<Loader2 className="animate-spin" />
											Chargement en cours...
										</div>
									</TableCell>
								</TableRow>
							) : table.getRowModel().rows?.length ? (
								table.getRowModel().rows.map((row, index) => (
									<TableRow
										key={row.id}
										className={clsx(
											isStaticCleanVariant
												? "bg-white"
												: isGrayVariant
												? "bg-gray-50"
												: index % 2 === 0
												? "bg-background-table-gray"
												: "bg-white",
											!isStaticCleanVariant && "transition-colors duration-150",
											onRowClick && "cursor-pointer hover:bg-gray-50/50"
										)}
										data-state={row.getIsSelected() && "selected"}
										onClick={onRowClick ? () => onRowClick(row.original) : undefined}
									>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id} className="p-4">
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										colSpan={columns.length}
										className="h-24 text-center bg-headerButtonActive"
									>
										<div className="flex flex-col items-center justify-center">
											{Icon && <Icon />}
											<span>{noResultsText}</span>
										</div>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>

			<div className="flex items-center justify-between space-x-2 py-4">
				<div className="flex items-center space-x-4">
					<Button
						variant="outline"
						size="sm"
						onClick={() => onPageChange(meta.page - 1)}
						disabled={!meta.hasPreviousPage || isLoading}
					>
						{isLoading ? (
							<>
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								Précédent
							</>
						) : (
							"Précédent"
						)}
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => onPageChange(meta.page + 1)}
						disabled={!meta.hasNextPage || isLoading}
					>
						{isLoading ? (
							<>
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								Suivant
							</>
						) : (
							"Suivant"
						)}
					</Button>
				</div>

				<div className="flex items-center gap-4">
					{onLimitChange && (
						<Select
							value={meta.limit.toString()}
							onValueChange={handleLimitChange}
						>
							<SelectTrigger className="w-32 text-sm" size="sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{limitOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
					<div className="text-sm text-muted-foreground flex-nowrap flex items-center whitespace-nowrap flex-shrink-0">
						<span>
							Page <strong>{meta.page} / {meta.totalPages}</strong>
						</span>
						<span className="ml-4">
							Total: <strong>{meta.total}</strong> résultats
						</span>
					</div>
				</div>
			</div>
		</div>
	);
}; 
